import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { FiServer, FiCheckCircle, FiXCircle } from 'react-icons/fi';

import bannerGif from '../../../../assets/images/banner.gif';
import { backendUrl } from '../../../../utilities/axios';
import { SkeletonCard, NoData } from '../../../shared/WebsiteFeedback';

/* ── TYPE LABELS ────────────────────────── */
const TYPE_LABELS = {
  oldschool:    { label: 'OLD SCHOOL',    bg: 'rgba(232,192,90,0.14)',  color: '#e8c05a', border: 'rgba(232,192,90,0.3)'  },
  newschool:    { label: 'NEW SCHOOL',    bg: 'rgba(147,51,234,0.14)',  color: '#a78bfa', border: 'rgba(147,51,234,0.3)'  },
  middleschool: { label: 'MIDDLE SCHOOL', bg: 'rgba(59,130,246,0.14)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
  custom:       { label: 'CUSTOM',        bg: 'rgba(239,68,68,0.14)',   color: '#f87171', border: 'rgba(239,68,68,0.3)'   },
};

const FAME_LABELS = {
  known:  { label: 'KNOWN',  bg: 'rgba(59,130,246,0.14)', color: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  famous: { label: 'FAMOUS', bg: 'rgba(232,192,90,0.14)', color: '#e8c05a', border: 'rgba(232,192,90,0.3)' },
};

const PAGE_SIZE = 12;

/* ── STYLED COMPONENTS ──────────────────── */
const PageWrapper = styled.div`
  padding: 18px 20px 48px;
  @media (max-width: 480px) { padding: 14px 12px 36px; }
`;

/* ── STATS ──────────────────────────────── */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const STAT_COLORS = {
  purple: { icon: 'rgba(147,51,234,0.15)', iconColor: '#a78bfa', border: 'rgba(147,51,234,0.22)', glow: 'rgba(147,51,234,0.08)' },
  green:  { icon: 'rgba(22,163,74,0.15)',  iconColor: '#4ade80', border: 'rgba(74,222,128,0.22)',  glow: 'rgba(22,163,74,0.08)'  },
  red:    { icon: 'rgba(239,68,68,0.15)',  iconColor: '#f87171', border: 'rgba(239,68,68,0.22)',   glow: 'rgba(239,68,68,0.08)'  },
};

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #1A1A1A;
  border: 1px solid ${p => STAT_COLORS[p.color]?.border};
  border-radius: 12px;
  box-shadow: 0 0 0 1px ${p => STAT_COLORS[p.color]?.glow} inset;

  .icon {
    width: 44px; height: 44px; border-radius: 11px; flex-shrink: 0;
    background: ${p => STAT_COLORS[p.color]?.icon};
    display: flex; align-items: center; justify-content: center;
    svg { font-size: 20px; color: ${p => STAT_COLORS[p.color]?.iconColor}; }
  }

  .label {
    font-size: 11.5px; color: #6b7280; margin-bottom: 4px;
    text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600;
  }

  .value {
    font-size: 26px; font-weight: 800; color: #f1f5f9;
    line-height: 1; font-variant-numeric: tabular-nums;
  }
`;

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const StatSkeleton = styled.div`
  height: 12px; border-radius: 4px; width: 60%;
  background: linear-gradient(90deg, #1e1e1e 25%, #2a2a2a 50%, #1e1e1e 75%);
  background-size: 1200px 100%;
  animation: ${shimmer} 1.6s infinite linear;
`;

/* ── FILTER BAR ─────────────────────────── */
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  padding: 7px 16px; border-radius: 8px; border: 1px solid;
  font-family: inherit; font-size: 12.5px; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
  background: ${p => p.active ? '#7C3AED' : 'transparent'};
  color:       ${p => p.active ? '#fff'    : '#6b7280'};
  border-color: ${p => p.active ? '#7C3AED' : 'rgba(255,255,255,0.08)'};
  &:hover { color: #c4b5fd; border-color: rgba(147,51,234,0.4); }
`;

const CountBadge = styled.span`
  margin-inline-start: auto;
  font-size: 12px; color: #4b5563;
`;

/* ── SERVERS GRID ───────────────────────── */
const ServersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; gap: 8px; }
`;

const ServerCard = styled.div`
  display: block; text-decoration: none;
  background: #161616;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px; overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
  &:hover {
    border-color: rgba(255,255,255,0.14);
    box-shadow: 0 4px 20px rgba(0,0,0,0.45);
    .srv-img img { transform: scale(1.05); }
  }

  .srv-img {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: #0a0a14;
    img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.45s ease; }
    &::after {
      content: ''; position: absolute; inset: 0;
      background: linear-gradient(to top, rgba(22,22,22,0.75) 0%, transparent 50%);
      pointer-events: none;
    }
  }

  .status-dot {
    position: absolute; top: 8px; right: 8px;
    width: 8px; height: 8px; border-radius: 50%;
    border: 2px solid #161616;
    &.active   { background: #4ade80; box-shadow: 0 0 6px #4ade8080; }
    &.inactive { background: #fbbf24; }
  }

  .card-body { padding: 10px 12px 12px; }

  .srv-name {
    font-size: 13px; font-weight: 700; color: #e2e8f0;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-bottom: 7px;
  }

  .meta { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 8px; }

  .type-badge {
    font-size: 9.5px; font-weight: 800; padding: 2px 7px; border-radius: 5px;
    letter-spacing: 0.06em;
  }

  .status-badge {
    font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 5px;
    &.active   { background: rgba(52,211,153,0.1); color: #34d399; border: 1px solid rgba(52,211,153,0.25); }
    &.inactive { background: rgba(251,191,36,0.1);  color: #fbbf24; border: 1px solid rgba(251,191,36,0.25); }
  }

  .stats-row {
    display: flex; gap: 10px;
    font-size: 11px; color: #4b5563; font-weight: 600;
    span { display: flex; align-items: center; gap: 3px; }
  }
`;

/* ── PAGINATION ─────────────────────────── */
const PaginationWrap = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  justify-content: center; margin-top: 28px;
`;

const PageBtn = styled.button`
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid;
  font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
  transition: all 0.15s;
  background:    ${p => p.active ? '#7C3AED' : 'transparent'};
  color:         ${p => p.active ? '#fff'    : '#6b7280'};
  border-color:  ${p => p.active ? '#7C3AED' : 'rgba(255,255,255,0.08)'};
  &:hover { border-color: rgba(147,51,234,0.5); color: #c4b5fd; }
`;

/* ── COMPONENT ──────────────────────────── */
const Archive = () => {
  const { t, i18n } = useTranslation();

  const [stats, setStats]           = useState({ total: 0, active: 0, inactive: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [servers, setServers]       = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [filter, setFilter]         = useState('all');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* fetch stats (counts only) */
  useEffect(() => {
    setStatsLoading(true);
    Promise.all([
      axios.get('/api/get_servers?page=1&size=1'),
      axios.get(`/api/get_servers?page=1&size=1&filter=${JSON.stringify({ is_active: true })}`),
      axios.get(`/api/get_servers?page=1&size=1&filter=${JSON.stringify({ is_active: false })}`),
    ]).then(([all, active, inactive]) => {
      setStats({
        total:    all.data.itemsCount    || 0,
        active:   active.data.itemsCount || 0,
        inactive: inactive.data.itemsCount || 0,
      });
    }).catch(() => {}).finally(() => setStatsLoading(false));
  }, []);

  /* fetch server list */
  useEffect(() => {
    setListLoading(true);
    const f = filter === 'all' ? {} : { is_active: filter === 'active' };
    axios.get(`/api/get_servers?page=${page}&size=${PAGE_SIZE}&filter=${JSON.stringify(f)}&sort=-createdAt`)
      .then(({ data }) => {
        setServers(data.data || []);
        setTotalCount(data.itemsCount || 0);
      })
      .catch(() => setServers([]))
      .finally(() => setListLoading(false));
  }, [filter, page]);

  const changeFilter = (f) => { setFilter(f); setPage(1); };

  const getImage = (srv) => srv.image ? `${backendUrl}/servers/${srv.image}` : bannerGif;
  const getName  = (srv) =>
    i18n.language === 'ar'
      ? (srv.name_ar || srv.name_en || '')
      : (srv.name_en || srv.name_ar || '');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
      <PageWrapper>

        {/* ── STATS ── */}
        <StatsGrid>
          <StatCard color="purple">
            <div className="icon"><FiServer /></div>
            <div>
              <div className="label">{t('website.archive.stat_total')}</div>
              {statsLoading ? <StatSkeleton /> : <div className="value">{stats.total.toLocaleString()}</div>}
            </div>
          </StatCard>

          <StatCard color="green">
            <div className="icon"><FiCheckCircle /></div>
            <div>
              <div className="label">{t('website.archive.stat_active')}</div>
              {statsLoading ? <StatSkeleton /> : <div className="value">{stats.active.toLocaleString()}</div>}
            </div>
          </StatCard>

          <StatCard color="red">
            <div className="icon"><FiXCircle /></div>
            <div>
              <div className="label">{t('website.archive.stat_inactive')}</div>
              {statsLoading ? <StatSkeleton /> : <div className="value">{stats.inactive.toLocaleString()}</div>}
            </div>
          </StatCard>
        </StatsGrid>

        {/* ── FILTER BAR ── */}
        <FilterBar>
          <FilterBtn active={filter === 'all'}      onClick={() => changeFilter('all')}>{t('website.archive.filter_all')}</FilterBtn>
          <FilterBtn active={filter === 'active'}   onClick={() => changeFilter('active')}>{t('website.archive.filter_active')}</FilterBtn>
          <FilterBtn active={filter === 'inactive'} onClick={() => changeFilter('inactive')}>{t('website.archive.filter_inactive')}</FilterBtn>
          {!listLoading && <CountBadge>{t('website.archive.servers_count', { count: totalCount.toLocaleString() })}</CountBadge>}
        </FilterBar>

        {/* ── SERVER LIST ── */}
        {listLoading ? (
          <ServersGrid>
            {Array.from({ length: PAGE_SIZE }, (_, i) => <SkeletonCard key={i} />)}
          </ServersGrid>
        ) : servers.length === 0 ? (
          <NoData />
        ) : (
          <ServersGrid>
            {servers.map(srv => {
              const ts = TYPE_LABELS[srv.type] || TYPE_LABELS.custom;
              return (
                <ServerCard key={srv._id} as={Link} to={`/server/${srv._id}`}>
                  <div className="srv-img">
                    <img
                      src={getImage(srv)}
                      alt={getName(srv)}
                      onError={e => { e.currentTarget.src = bannerGif; }}
                    />
                    <span className={`status-dot ${srv.is_active ? 'active' : 'inactive'}`} />
                  </div>
                  <div className="card-body">
                    <div className="srv-name">{getName(srv)}</div>
                    <div className="meta">
                      <span
                        className="type-badge"
                        style={{ background: ts.bg, color: ts.color, border: `1px solid ${ts.border}` }}
                      >
                        {ts.label}
                      </span>
                      <span className={`status-badge ${srv.is_active ? 'active' : 'inactive'}`}>
                        {srv.is_active ? t('website.archive.status_active') : t('website.archive.status_inactive')}
                      </span>
                      {FAME_LABELS[srv.fame] && (
                        <span
                          className="type-badge"
                          style={{ background: FAME_LABELS[srv.fame].bg, color: FAME_LABELS[srv.fame].color, border: `1px solid ${FAME_LABELS[srv.fame].border}` }}
                        >
                          {FAME_LABELS[srv.fame].label}
                        </span>
                      )}
                    </div>
                    <div className="stats-row">
                      <span>Lv. {srv.level ?? '—'}</span>
                      <span>❤ {(srv.votes || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </ServerCard>
              );
            })}
          </ServersGrid>
        )}

        {/* ── PAGINATION ── */}
        {totalPages > 1 && (
          <PaginationWrap>
            {Array.from({ length: Math.min(totalPages, 20) }, (_, i) => (
              <PageBtn key={i} active={page === i + 1} onClick={() => setPage(i + 1)}>
                {i + 1}
              </PageBtn>
            ))}
          </PaginationWrap>
        )}

      </PageWrapper>
  );
};

export default Archive;
